import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { helpers } from '@vuelidate/validators';
import { getAgeByDate } from './date-utils';

export const isValidPhoneNumber = (value: string): boolean =>
  /^(\s*)?(\+)?([- _():=+]?\d[- _():=+]?){11}(\s*)?$/.test(value);

const dateRegex =
  /^(?:(?:31(\/|-|\.)(?:0?[13578]|1[02]))\1|(?:(?:29|30)(\/|-|\.)(?:0?[13-9]|1[0-2])\2))(?:(?:1[6-9]|[2-9]\d)?\d{2})$|^(?:29(\/|-|\.)0?2\3(?:(?:(?:1[6-9]|[2-9]\d)?(?:0[48]|[2468][048]|[13579][26])|(?:(?:16|[2468][048]|[3579][26])00))))$|^(?:0?[1-9]|1\d|2[0-8])(\/|-|\.)(?:(?:0?[1-9])|(?:1[0-2]))\4(?:(?:1[6-9]|[2-9]\d)?\d{4})$/;
export const isValidDate = (value: string) => dateRegex.test(value);
export const isNotExpired = (
  dateToCheck: dayjs.ConfigType,
  dateToCompare: string,
) => dayjs(dateToCheck).isAfter(dateToCompare);
export const isAfterOrEqual = (
  dateToCheck: dayjs.ConfigType,
  dateToCompare: string,
) => !dayjs(dateToCheck).isAfter(dateToCompare);

const alphaHyphenRegex = /^[a-zA-Z- ]*$/;
const alphaNumCyrillicRegex = /^[a-zA-Zа-яА-Я0-9]*$/;
const alphaNumSpaceRegex = /^[a-zA-Z0-9 ]*$/;
export const cyrillicRegex = /^[а-яА-ЯёЁ]*$/;
export const cyrillicWithKazakhLettersRegex =
  /^[а-яА-ЯёЁ\u04D8\u04D9\u04B0\u04B1\u0406\u0456\u04A2\u04A3\u0492\u0493\u04AE\u04AF\u049A\u049B\u04E8\u04E9\u04BA\u04BB]*$/;

export const isAlphaNumCyrillic = (value: string) =>
  alphaNumCyrillicRegex.test(value);

export const isAlphaCyrillic = (value: string) => cyrillicRegex.test(value);
export const isAlphaCyrillicWithKazakhLetters = (value: string) =>
  cyrillicWithKazakhLettersRegex.test(value);

export const isAlphaHyphen = (value: string) => alphaHyphenRegex.test(value);

export const isAlphaNumSpace = (value: string) =>
  alphaNumSpaceRegex.test(value);

const documentSeriesRegex = /^[a-zA-Z0-9]{1,2}$/;
export const isValidDocumentSeries = (value: string) =>
  value === '' || documentSeriesRegex.test(value);

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);

export type AgeConstraints = {
  min: number;
  max: number;
  dateToGetAge: string;
};

/**
 * Return extra data for custom message localization
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#returning-extra-data-from-validators
 * @see https://vuelidate-next.netlify.app/advanced_usage.html#customising-the-i18n-message
 */
export const withMessagePath =
  (validator: (value: string) => boolean, messagePath?: string) =>
  (value: string) => ({
    $valid: validator(value),
    messagePath,
  });

/**
 * Min and max age validation messages differ based on passenger type.
 * To display different validation messages for single validator we use messagePath
 */
export const minAge = (ageConstraints: AgeConstraints) => (dob: string) => {
  const { age, isPaxBornByDepartureDate } = getAgeByDate(
    dob,
    ageConstraints.dateToGetAge,
  );
  return isPaxBornByDepartureDate && age >= ageConstraints.min;
};

export const initMinAgeValidator = (
  { min, max, dateToGetAge }: AgeConstraints,
  messagePath?: string,
) => {
  const minAgeValidator = minAge({ min, max, dateToGetAge });
  return helpers.withParams(
    { min, max },
    withMessagePath(minAgeValidator, messagePath),
  );
};

export const maxAge = (ageConstraints: AgeConstraints) => (dob: string) => {
  const constraintsNextDay = dayjs(ageConstraints.dateToGetAge)
    .add(1, 'day')
    .toString();
  const { age } = getAgeByDate(dob, constraintsNextDay);
  return age <= ageConstraints.max;
};

export const initMaxAgeValidator = (
  { min, max, dateToGetAge }: AgeConstraints,
  messagePath?: string,
) => {
  const maxAgeValidator = maxAge({ min, max, dateToGetAge });
  return helpers.withParams(
    { min, max },
    withMessagePath(maxAgeValidator, messagePath),
  );
};
